import { BoardSelectDialog } from '@/features/board/BoardSelectDialog';
import { BoardManagerDialog } from '@/features/board/BoardManagerDialog';
import { resolveUploadPortPath } from '@/features/upload/resolveUploadPort';
import { useBoardStore } from '@/state/boardStore';
import { useSerialStore } from '@/state/serialStore';
import { useSettingsStore } from '@/state/settingsStore';
import { useUploadStore } from '@/state/uploadStore';
import { useWorkspaceStore } from '@/state/workspaceStore';
import { SKYROVER_COLORS } from '@/theme/skyroverTheme';
import { examples } from '@skyrover/examples';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadIcon from '@mui/icons-material/Upload';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';

interface HeaderProps {
  onNewProject: () => void;
  onOpenProject: () => void;
  onSaveProject: () => void;
  onSaveProjectAs: () => void;
  onLoadExample: (exampleId: string) => void;
  onRegenerateCode: () => void;
}

export function Header({
  onNewProject,
  onOpenProject,
  onSaveProject,
  onSaveProjectAs,
  onLoadExample,
  onRegenerateCode,
}: HeaderProps) {
  const { boardId, allBoards, setBoardId } = useBoardStore();
  const {
    port,
    availablePorts,
    connected,
    connecting,
    pausedForUpload,
    portsLoading,
    webSerialSupported,
    setPort,
    connect,
    disconnect,
    refreshPorts,
    addWebSerialDevice,
    getSelectedPortLabel,
  } = useSerialStore();
  const generatedCode = useWorkspaceStore((state) => state.generatedCode);
  const blockOperationMode = useSettingsStore((state) => state.blockOperationMode);
  const setBlockOperationMode = useSettingsStore((state) => state.setBlockOperationMode);
  const uploading = useUploadStore((state) => state.uploading);
  const upload = useUploadStore((state) => state.upload);
  const uploadPortPath = useMemo(
    () => resolveUploadPortPath(port, availablePorts),
    [port, availablePorts],
  );
  const canUpload =
    connected && Boolean(uploadPortPath && generatedCode.trim()) && !uploading;
  const serialLinked = connected || pausedForUpload;
  const serialButtonLabel = uploading && serialLinked
    ? 'Connected'
    : connecting
      ? 'Connecting…'
      : serialLinked
        ? 'Disconnect'
        : 'Connect';
  const [filesAnchor, setFilesAnchor] = useState<null | HTMLElement>(null);
  const [examplesAnchor, setExamplesAnchor] = useState<null | HTMLElement>(null);
  const [boardDialogOpen, setBoardDialogOpen] = useState(false);
  const [boardManagerOpen, setBoardManagerOpen] = useState(false);

  const currentBoard = useMemo(
    () => allBoards.find((board) => board.id === boardId),
    [allBoards, boardId],
  );

  const handleBoardConfirm = (nextBoardId: string) => {
    if (nextBoardId !== boardId) {
      setBoardId(nextBoardId);
      onRegenerateCode();
    }
  };

  const closeFilesMenu = () => setFilesAnchor(null);

  const pillSx = {
    bgcolor: SKYROVER_COLORS.pillBg,
    color: '#fff',
    border: `1px solid ${SKYROVER_COLORS.pillBorder}`,
    borderRadius: '20px',
    height: 32,
    fontSize: 12,
    fontWeight: 600,
    '& .MuiSelect-select': { py: 0.5, px: 1.5 },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
  };

  return (
    <Box
      component="header"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2,
        minHeight: 52,
        bgcolor: SKYROVER_COLORS.header,
        color: '#fff',
        flexShrink: 0,
      }}
    >
      <Typography variant="h6" fontWeight={800} letterSpacing={-0.5} sx={{ fontSize: 18 }}>
        skyrover<span style={{ fontWeight: 400 }}>.ai</span>
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 2 }}>
        <Button
          color="inherit"
          endIcon={<KeyboardArrowDownIcon />}
          onClick={(e) => setFilesAnchor(e.currentTarget)}
          sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5 }}
        >
          FILES
        </Button>
        <Menu anchorEl={filesAnchor} open={Boolean(filesAnchor)} onClose={closeFilesMenu}>
          <MenuItem
            onClick={() => {
              closeFilesMenu();
              onNewProject();
            }}
          >
            New Project
          </MenuItem>
          <MenuItem
            onClick={() => {
              closeFilesMenu();
              onOpenProject();
            }}
          >
            Open Project
          </MenuItem>
          <MenuItem
            onClick={() => {
              closeFilesMenu();
              onSaveProject();
            }}
          >
            Save Project
          </MenuItem>
          <MenuItem
            onClick={() => {
              closeFilesMenu();
              onSaveProjectAs();
            }}
          >
            Save Project As
          </MenuItem>
          <MenuItem
            onClick={(e) => {
              closeFilesMenu();
              setExamplesAnchor(e.currentTarget);
            }}
          >
            Examples
          </MenuItem>
        </Menu>
        <Menu anchorEl={examplesAnchor} open={Boolean(examplesAnchor)} onClose={() => setExamplesAnchor(null)}>
          {examples.map((ex) => (
            <MenuItem
              key={ex.id}
              onClick={() => {
                onLoadExample(ex.id);
                setExamplesAnchor(null);
              }}
            >
              {ex.name}
            </MenuItem>
          ))}
        </Menu>

        <Typography
          component="button"
          type="button"
          onClick={() => setBoardManagerOpen(true)}
          sx={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 0.5,
            cursor: 'pointer',
            opacity: 0.9,
            border: 'none',
            bgcolor: 'transparent',
            color: 'inherit',
            p: 0,
            fontFamily: 'inherit',
            '&:hover': { opacity: 1, textDecoration: 'underline' },
          }}
        >
          BOARD MANAGER
        </Typography>
        <Typography
          component="span"
          sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, cursor: 'default', opacity: 0.9 }}
        >
          TUTORIALS
        </Typography>
        <Typography
          component="span"
          sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, cursor: 'default', opacity: 0.9 }}
        >
          ABOUT
        </Typography>
      </Box>

      <Box sx={{ flex: 1 }} />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: SKYROVER_COLORS.pillBg,
          border: `1px solid ${SKYROVER_COLORS.pillBorder}`,
          borderRadius: '20px',
          px: 1,
          py: 0.5,
          flexShrink: 0,
        }}
        role="group"
        aria-label="Block operation mode"
      >
        <Typography
          sx={{
            color: '#fff',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.5,
            pl: 0.5,
          }}
        >
          MODE
        </Typography>
        <Box
          sx={{
            position: 'relative',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            alignItems: 'center',
            width: 168,
            height: 28,
            borderRadius: '14px',
            bgcolor: 'rgba(0, 0, 0, 0.16)',
            p: '2px',
            boxSizing: 'border-box',
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: 2,
              left: blockOperationMode === 'beginner' ? 2 : '50%',
              width: 'calc(50% - 2px)',
              height: 'calc(100% - 4px)',
              borderRadius: '12px',
              bgcolor: '#fff',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.18)',
              transition: 'left 0.2s ease',
            }}
          />
          <Button
            size="small"
            onClick={() => setBlockOperationMode('beginner')}
            aria-pressed={blockOperationMode === 'beginner'}
            sx={{
              zIndex: 1,
              minWidth: 0,
              px: 1,
              py: 0.25,
              borderRadius: '12px',
              fontSize: 11,
              fontWeight: 700,
              lineHeight: 1.2,
              textTransform: 'none',
              color: blockOperationMode === 'beginner' ? SKYROVER_COLORS.header : '#fff',
              '&:hover': { bgcolor: 'transparent' },
            }}
          >
            Beginner
          </Button>
          <Button
            size="small"
            onClick={() => setBlockOperationMode('advanced')}
            aria-pressed={blockOperationMode === 'advanced'}
            sx={{
              zIndex: 1,
              minWidth: 0,
              px: 1,
              py: 0.25,
              borderRadius: '12px',
              fontSize: 11,
              fontWeight: 700,
              lineHeight: 1.2,
              textTransform: 'none',
              color: blockOperationMode === 'advanced' ? SKYROVER_COLORS.header : '#fff',
              '&:hover': { bgcolor: 'transparent' },
            }}
          >
            Advanced
          </Button>
        </Box>
      </Box>

      <Button
        size="small"
        color="inherit"
        endIcon={<KeyboardArrowDownIcon />}
        onClick={() => setBoardDialogOpen(true)}
        sx={{
          ...pillSx,
          minWidth: 130,
          textTransform: 'none',
          justifyContent: 'space-between',
        }}
        aria-label="Board"
      >
        {currentBoard?.displayName ?? 'Select board'}
      </Button>

      <BoardSelectDialog
        open={boardDialogOpen}
        boards={allBoards}
        selectedBoardId={boardId}
        onClose={() => setBoardDialogOpen(false)}
        onConfirm={handleBoardConfirm}
      />

      <BoardManagerDialog open={boardManagerOpen} onClose={() => setBoardManagerOpen(false)} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Select
          size="small"
          value={port || ''}
          displayEmpty
          onChange={(e) => {
            const value = e.target.value;
            if (value === '__add_device__') {
              void addWebSerialDevice();
              return;
            }
            setPort(value);
          }}
          disabled={serialLinked || portsLoading || uploading}
          renderValue={() => {
            if (portsLoading) {
              return 'Scanning…';
            }
            if (!port) {
              return 'Select port';
            }
            return getSelectedPortLabel();
          }}
          sx={{ ...pillSx, minWidth: 120 }}
          aria-label="COM Port"
        >
          {webSerialSupported ? (
            <MenuItem value="__add_device__" sx={{ fontWeight: 600 }}>
              Select device…
            </MenuItem>
          ) : null}
          {availablePorts.length === 0 ? (
            <MenuItem disabled value="">
              {webSerialSupported ? 'No ports — refresh or select device' : 'Use Chrome or Edge'}
            </MenuItem>
          ) : (
            availablePorts.map((entry) => (
              <MenuItem key={entry.id} value={entry.id}>
                {entry.label}
              </MenuItem>
            ))
          )}
        </Select>

        <Tooltip title="Refresh COM ports">
          <span>
            <IconButton
              size="small"
              color="inherit"
              onClick={() => void refreshPorts()}
              disabled={serialLinked || portsLoading || uploading}
              aria-label="Refresh COM ports"
              sx={{
                border: `1px solid ${SKYROVER_COLORS.pillBorder}`,
                width: 32,
                height: 32,
              }}
            >
              {portsLoading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>

        <Button
          size="small"
          color="inherit"
          onClick={() => void (serialLinked ? disconnect() : connect())}
          disabled={connecting || uploading || (!serialLinked && !webSerialSupported)}
          sx={{
            ...pillSx,
            minWidth: 88,
            textTransform: 'none',
          }}
          aria-label={serialLinked ? 'Disconnect board' : 'Connect board'}
        >
          {serialButtonLabel}
        </Button>
      </Box>

      <Button
        variant="contained"
        startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
        disabled={!canUpload}
        onClick={() => void upload()}
        sx={{
          bgcolor: '#fff',
          color: SKYROVER_COLORS.header,
          borderRadius: '20px',
          px: 2.5,
          minWidth: 108,
          '&:hover': { bgcolor: '#f0f0f0' },
          '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.5)', color: 'rgba(255,255,255,0.7)' },
        }}
      >
        {uploading ? 'Uploading…' : 'Upload'}
      </Button>
    </Box>
  );
}
